// Integration vocabulary. Per-integration field schemas + the parity checker
// every consumer of `data-pf-*` runs against this catalog at boot.
//
// The integration field keys (e.g. "rid", "handle") are the contract that
// connects four places at runtime:
//   1. The wizard renders form inputs whose names match the keys.
//   2. The Manage tab saves them to jobs/{siteId}.integrationConfig[id][key].
//   3. site-api ships those into window.PROTOFORGE_INTEGRATIONS[id][key].
//   4. The agent emits data-pf-template-href="<id>.<key>|<urlTemplate>".
// Everything must agree. Defining them once here is what makes that possible.
export const INTEGRATIONS = [
    // ── Restaurants ─────────────────────────────────────────────────────────
    { id: "opentable", label: "OpenTable", note: "Reservations",
        businessTypes: ["restaurant"], kind: "embed", color: "#DA3743",
        fields: [{ key: "rid", label: "Restaurant ID (RID)", placeholder: "https://www.opentable.com/widget/...?rid=12345",
                hint: "Paste an official OpenTable widget or restref URL containing rid= or restref=." }] },
    { id: "resy", label: "Resy", note: "Reservations",
        businessTypes: ["restaurant"], kind: "embed", color: "#FF4D4D",
        fields: [{ key: "venue_slug", label: "Resy URL path", placeholder: "ny/restaurant-name",
                hint: "Paste the full Resy venue URL; the city and venue are extracted automatically." }] },
    { id: "toast", label: "Toast", note: "Online ordering / POS",
        businessTypes: ["restaurant"], kind: "embed", color: "#FF4F0A",
        fields: [{ key: "slug", label: "Restaurant slug", placeholder: "jane-bakery-columbia",
                hint: "From your Toast URL: toasttab.com/<slug>" }] },
    { id: "doordash", label: "DoorDash", note: "Delivery",
        businessTypes: ["restaurant"], kind: "embed", color: "#FF3008",
        fields: [{ key: "store_id", label: "Store ID", placeholder: "12345678",
                hint: "From your DoorDash store URL: doordash.com/store/<id>" }] },
    { id: "ubereats", label: "Uber Eats", note: "Delivery",
        businessTypes: ["restaurant"], kind: "embed", color: "#000000",
        fields: [{ key: "store_slug", label: "Store slug", placeholder: "jane-bakery/abc123",
                hint: "From your Uber Eats URL: ubereats.com/store/<slug>" }] },
    { id: "grubhub", label: "Grubhub", note: "Delivery",
        businessTypes: ["restaurant"], kind: "embed", color: "#F63440",
        fields: [{ key: "slug", label: "Restaurant slug", placeholder: "jane-bakery-columbia",
                hint: "From your Grubhub URL: grubhub.com/restaurant/<slug>" }] },
    // ── Retail / commerce ───────────────────────────────────────────────────
    { id: "shopify", label: "Shopify", note: "Sync your existing store",
        businessTypes: ["retail"], kind: "embed", color: "#7AB55C",
        fields: [{ key: "shop", label: "Shop subdomain", placeholder: "jane-bakery",
                hint: "Without .myshopify.com" }] },
    { id: "etsy", label: "Etsy", note: "Sync listings",
        businessTypes: ["retail", "creative"], kind: "embed", color: "#F1641E",
        fields: [{ key: "shopname", label: "Shop name", placeholder: "JanesPottery",
                hint: "From etsy.com/shop/<shopname>" }] },
    { id: "amazon", label: "Amazon", note: "Storefront link",
        businessTypes: ["retail"], kind: "embed", color: "#FF9900",
        fields: [{ key: "page_id", label: "Storefront page ID", placeholder: "abcd1234" }] },
    { id: "tiktok-shop", label: "TikTok Shop", note: "Sync products",
        businessTypes: ["retail"], kind: "embed", color: "#000000",
        fields: [{ key: "handle", label: "Handle", placeholder: "janebakery", hint: "Without the @" }] },
    // ── Booking / scheduling ────────────────────────────────────────────────
    { id: "calendly", label: "Calendly", note: "Appointment booking",
        businessTypes: ["service", "professional", "creative", "health"], kind: "embed", color: "#006BFF",
        fields: [{ key: "handle", label: "Calendly handle", placeholder: "jane-smith",
                hint: "The part after calendly.com/" }] },
    { id: "acuity", label: "Acuity", note: "Scheduling",
        businessTypes: ["service", "professional", "health"], kind: "embed", color: "#1E1F71",
        fields: [{ key: "owner_id", label: "Owner ID", placeholder: "12345678",
                hint: "From your Acuity dashboard URL" }] },
    { id: "google-calendar", label: "Google Calendar", note: "Availability",
        businessTypes: ["service", "professional"], kind: "embed", color: "#4285F4",
        fields: [{ key: "cal_id", label: "Calendar ID", placeholder: "you@gmail.com or shared cal ID" }] },
    { id: "square-appointments", label: "Square Appointments", note: "Booking",
        businessTypes: ["health"], kind: "embed", color: "#000000",
        fields: [{ key: "site_id", label: "Site ID", placeholder: "abcd1234" }] },
    { id: "simplepractice", label: "SimplePractice", note: "EHR + booking",
        businessTypes: ["health"], kind: "embed", color: "#3DA5D9",
        fields: [{ key: "booking_url", label: "Public booking URL", placeholder: "https://...",
                hint: "Paste the full URL from SimplePractice" }] },
    { id: "mindbody", label: "Mindbody", note: "Class booking",
        businessTypes: ["health"], kind: "embed", color: "#612B79",
        fields: [{ key: "studio_id", label: "Studio ID", placeholder: "12345" }] },
    // ── Discovery / reviews / social ────────────────────────────────────────
    { id: "google-business", label: "Google Business", note: "Hours, reviews, map",
        businessTypes: ["restaurant", "retail", "service", "professional", "health", "other"],
        kind: "embed", color: "#4285F4",
        fields: [{ key: "place_id", label: "Place ID (optional)", placeholder: "ChIJ...",
                hint: "Only needed for the reviews link. We use the address for the map." }] },
    { id: "yelp", label: "Yelp", note: "Reviews badge",
        businessTypes: ["restaurant"], kind: "embed", color: "#D32323",
        fields: [{ key: "slug", label: "Yelp slug", placeholder: "jane-bakery-columbia",
                hint: "From your Yelp URL: yelp.com/biz/<slug>" }] },
    { id: "thumbtack", label: "Thumbtack", note: "Lead funnel",
        businessTypes: ["service"], kind: "embed", color: "#009FD9",
        fields: [{ key: "slug", label: "Profile slug", placeholder: "jane-plumbing" }] },
    { id: "angi", label: "Angi", note: "Lead funnel",
        businessTypes: ["service"], kind: "embed", color: "#FF6900",
        fields: [{ key: "id", label: "Listing ID", placeholder: "12345" }] },
    { id: "instagram", label: "Instagram", note: "Photo feed / link",
        businessTypes: ["restaurant", "retail", "creative", "other"], kind: "embed", color: "#E1306C",
        fields: [{ key: "handle", label: "Handle", placeholder: "janebakery", hint: "Without the @" }] },
    { id: "tiktok", label: "TikTok", note: "Latest videos",
        businessTypes: ["restaurant", "creative"], kind: "embed", color: "#000000",
        fields: [{ key: "handle", label: "Handle", placeholder: "janebakery", hint: "Without the @" }] },
    { id: "linkedin", label: "LinkedIn", note: "Bio link",
        businessTypes: ["professional"], kind: "embed", color: "#0A66C2",
        fields: [{ key: "handle", label: "Handle or company slug", placeholder: "jane-smith" }] },
    { id: "behance", label: "Behance", note: "Portfolio",
        businessTypes: ["creative"], kind: "embed", color: "#1769FF",
        fields: [{ key: "handle", label: "Handle", placeholder: "janesmith" }] },
    { id: "pixieset", label: "Pixieset", note: "Photo galleries",
        businessTypes: ["creative"], kind: "embed", color: "#000000",
        fields: [{ key: "handle", label: "Handle", placeholder: "janesmith" }] },
    { id: "format", label: "Format", note: "Portfolio sync",
        businessTypes: ["creative"], kind: "embed", color: "#000000",
        fields: [{ key: "handle", label: "Handle", placeholder: "janesmith" }] },
    // ── Documents / contracts ───────────────────────────────────────────────
    { id: "docusign", label: "DocuSign", note: "Contracts",
        businessTypes: ["professional"], kind: "embed", color: "#FFCC22",
        fields: [{ key: "template_url", label: "Template URL", placeholder: "https://...",
                hint: "From DocuSign — the public template link" }] },
    // ── Backend (no plug-in fields; managed in dedicated tabs) ─────────────
    { id: "stripe", label: "Stripe", note: "Online payments",
        businessTypes: ["restaurant", "retail", "service", "professional", "creative", "health", "other"],
        kind: "backend", color: "#635BFF", fields: [] },
    // Square is primarily backend (POS / payments), but owners with Square
    // Online Checkout pages can paste a per-product URL we link "Buy" to.
    { id: "square", label: "Square", note: "Payments / POS",
        businessTypes: ["restaurant", "retail", "service"], kind: "backend", color: "#000000",
        fields: [{ key: "checkout_url", label: "Square Checkout URL (optional)", placeholder: "https://checkout.square.site/...",
                hint: "Optional. If you've made a Square Online Checkout link, paste it here and the storefront 'Buy' buttons will use it." }] },
    { id: "mailchimp", label: "Mailchimp", note: "Email list",
        businessTypes: ["restaurant", "retail", "professional", "creative", "other"],
        kind: "backend", color: "#FFE01B", fields: [] },
    { id: "klaviyo", label: "Klaviyo", note: "Email + SMS",
        businessTypes: ["retail"], kind: "backend", color: "#000000", fields: [] },
    { id: "twilio", label: "Twilio SMS", note: "Order updates",
        businessTypes: ["restaurant", "retail", "service"], kind: "backend", color: "#F22F46", fields: [] },
    { id: "quickbooks", label: "QuickBooks", note: "Invoicing",
        businessTypes: ["service", "professional"], kind: "backend", color: "#2CA01C", fields: [] },
    { id: "jobber", label: "Jobber", note: "Field service ops",
        businessTypes: ["service"], kind: "backend", color: "#1A7B43", fields: [] },
    { id: "housecall-pro", label: "Housecall Pro", note: "Dispatch + invoicing",
        businessTypes: ["service"], kind: "backend", color: "#0066CC", fields: [] },
];
export function getIntegration(id) {
    return INTEGRATIONS.find((i) => i.id === id);
}
export function integrationsForType(type) {
    return INTEGRATIONS.filter((i) => i.businessTypes.includes(type));
}
// Sanity check used by tests + dev assertions: every key referenced in agent
// HTML scaffolding (data-pf-requires / data-pf-template-*) must resolve to a
// declared field key here. Returns the list of unresolved keys, or [] if OK.
export function unresolvedIntegrationKeys(referenced) {
    const out = [];
    for (const ref of referenced) {
        const [id, key] = ref.split(".");
        if (!id || !key) {
            out.push(ref);
            continue;
        }
        const def = getIntegration(id);
        if (!def) {
            out.push(ref);
            continue;
        }
        if (!def.fields.some((f) => f.key === key))
            out.push(ref);
    }
    return out;
}
